import React from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { ProductCardTextAlignmentOption } from 'types/graphql';
import { DefaultImages } from 'shared/constants';
import { FontLoader } from 'src/components/font-loader';
import { MobileAppPreviewProduct, MobileAppProductCard } from 'src/dispensary-admin/components/product-card';
import { PreviewData } from '../components/preview';
import { usePreview } from '../components/preview/use-preview';

// Two cards rather than one: only a single tag renders over the image and a discount always wins
// over Staff Pick, so a discounted card alone would leave the Show Staff Pick toggle with nothing
// to preview. A pair also shows the app's row behavior -- both cards grow to the tallest one.
const discountedProduct: MobileAppPreviewProduct = {
  name: 'Blackberry Lemonade Flavored Cartridge',
  brandName: 'Hush',
  imageUrl: DefaultImages.Vaporizers ?? '',
  strainType: 'Hybrid',
  potency: ['THC: 64%', 'CBD: 0.41%', 'TERPS: 3.98%'],
  effects: 'Calm, Happy, Relaxed, Energetic, Focused, Uplifted',
  discountName: 'Vapes | 1G 4 For $60',
  collection: 'Monthly Weekly Daily Deals Exclusive Collection',
  price: 16.66,
  originalPrice: 27.77,
  discountLabel: '40% off',
  weight: '1g',
};

const staffPickProduct: MobileAppPreviewProduct = {
  name: 'Eggroll',
  brandName: 'RI Finest Gardens',
  imageUrl: DefaultImages.Flower ?? '',
  strainType: 'Indica-Hybrid',
  potency: ['THC: 19.3%'],
  effects: 'Sleepy, Hungry',
  discountName: 'Buy 2 Get 1 Mix & Match',
  collection: 'Staff Favorites',
  price: 33,
  weight: '1/8oz',
  isStaffPick: true,
};

type ProductCardPreviewProps = {
  data: PreviewData;
  showMobileAppPreview?: boolean;
  /** Hides the desktop Card View / List View figures, showing only the Mobile App Card View
   *  figure -- used alongside ProductCardsFormProps.mobileOnly. */
  mobileOnly?: boolean;
};

export const ProductCardPreview = ({
  data,
  showMobileAppPreview = false,
  mobileOnly = false,
}: ProductCardPreviewProps): JSX.Element => {
  const { textAlignment, showProductWeight, mobileAppDisplayOptions } = data.productCards;
  // Without an explicit theme, the card's add-to-cart button falls back to whatever ambient
  // theme happens to be in context above this preview -- not the
  // dispensary's actual configured "Buttons and Links" color. `usePreview` is the same
  // data-to-theme derivation Theme's own preview uses, so the mobile card preview's button
  // matches what actually renders on the mobile app.
  const { theme, encodedFonts } = usePreview(data);

  // Strain type pill colors apply to the web marketplace card too, independent of the mobile
  // app preview below.
  const {
    colors: { strainTypes },
  } = data;

  const advancedCustomizationOverrides = {
    backgroundColor: mobileAppDisplayOptions.backgroundColor,
    brandColor: mobileAppDisplayOptions.brandColor,
    potencyColor: mobileAppDisplayOptions.potencyColor,
    potencyDisplay: mobileAppDisplayOptions.potencyDisplay,
    priceColor: mobileAppDisplayOptions.priceColor,
    productNameColor: mobileAppDisplayOptions.productNameColor,
    strainTypeDisplay: mobileAppDisplayOptions.strainTypeDisplay,
    strikethroughPriceColor: mobileAppDisplayOptions.strikethroughPriceColor,
    weightColor: mobileAppDisplayOptions.weightColor,
  };

  let cardImage = '';

  if (textAlignment === ProductCardTextAlignmentOption.center) {
    cardImage = showProductWeight ? 'card-preview-weight-center.png' : 'card-preview-no-weight-center.png';
  } else {
    cardImage = showProductWeight ? 'card-preview-weight-v2.png' : 'card-preview-no-weight.png';
  }

  return (
    <Container>
      {!mobileOnly && (
        <>
          <figure>
            <Caption>Card View</Caption>
            <Image src={`https://images.dutchie.com/product-cards/${cardImage}`} alt='Card View Preview' width={184} />
          </figure>
          <figure>
            <Caption>List View</Caption>
            <Image
              src='https://images.dutchie.com/product-cards/list-preview-v2.png'
              alt='List View Preview'
              width={346}
            />
          </figure>
        </>
      )}

      {showMobileAppPreview && (
        <figure>
          <Caption>Mobile App Card View</Caption>
          {/* This figure renders outside Preview, so it loads the fonts named by its theme directly. */}
          <FontLoader encodedFonts={encodedFonts} />
          <ThemeProvider theme={theme}>
            <MobileCards>
              {[discountedProduct, staffPickProduct].map((product) => (
                <MobileAppProductCard
                  key={product.name}
                  product={product}
                  strainTypes={strainTypes}
                  textAlignment={textAlignment}
                  buttonFlair={data.mobile?.buttonFlair ?? undefined}
                  displayOptions={{
                    ...mobileAppDisplayOptions,
                    ...advancedCustomizationOverrides,
                    showProductWeight: mobileAppDisplayOptions.showProductWeight ?? showProductWeight,
                  }}
                />
              ))}
            </MobileCards>
          </ThemeProvider>
        </figure>
      )}
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Image = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
`;

const Caption = styled.figcaption`
  font-size: 12px;
  line-height: ${18 / 12};
  margin-bottom: 8px;
  text-align: center;
`;

// Capped so each card lands near the ~163px the app renders at on a phone. MobileAppProductCard's
// type scale is 1:1 with the app's, so letting the cards stretch to a full-width preview column
// would make the text read far smaller than it does in the app. `stretch` (the flex default) is
// what grows both cards to the tallest one.
const MobileCards = styled.div`
  display: flex;
  gap: 12px;
  max-width: 338px;
  width: 100%;

  > * {
    flex: 1;
    min-width: 0;
  }
`;
